import React from 'react';
import { Modal } from '@/components/Modals/modal';
import { ModalDataTypes } from '@/context/types';
import { ModalBody, ModalImpl } from '../ModalFactory';

type ConfirmModalData = ModalDataTypes['ConfirmAction'];

export const ConfirmModal: ModalImpl = (props) => {
  return (
    <Modal
      variant="dialog"
      ariaLabel="Confirmation Required"
      darkenOverlay={true}
      {...props}
    >
      {ConfirmBody}
    </Modal>
  );
};

const ConfirmBody: ModalBody<ConfirmModalData> = ({
  withModalClose,
  modalData,
}) => {
  // Default fallbacks in case they aren't provided
  const {
    title = 'Are you sure?',
    message = 'Please confirm your action.',
    confirmText = 'Yes, Proceed',
    cancelText = 'Cancel',
    onConfirm,
    onCancel,
  } = modalData ?? {};

  // Wrap the callbacks in withModalClose so the modal vanishes automatically upon selection
  const handleConfirm = withModalClose(async () => {
    if (onConfirm) await onConfirm();
  });

  const handleCancel = withModalClose(async () => {
    if (onCancel) await onCancel();
  });

  return (
    <div className="flex flex-col items-center px-2 py-4">
      {/* Icon (Optional: A warning or location pin icon fits nicely here) */}
      <div className="w-12 h-12 rounded-full bg-brand-truffle/10 flex items-center justify-center mb-4 text-brand-truffle">
        <svg
          className="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </div>

      <h3 className="font-display text-2xl font-bold text-text-heading mb-2">
        {title}
      </h3>

      <p className="font-body text-text-muted text-[15px] leading-relaxed mb-8">
        {message}
      </p>

      {/* Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 w-full">
        <button
          onClick={handleCancel}
          className="flex-1 py-3 px-4 rounded-full border border-brand-truffle/20 text-brand-truffle font-bold text-[15px] hover:bg-brand-truffle/5 transition-colors"
        >
          {cancelText}
        </button>
        <button
          onClick={handleConfirm}
          className="flex-1 py-3 px-4 rounded-full bg-brand-truffle text-brand-cream font-bold text-[15px] shadow-lift hover:opacity-90 transition-opacity"
        >
          {confirmText}
        </button>
      </div>
    </div>
  );
};
